import { ShieldCheck, ArrowRight } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../ui/accordion";

const securityBadges = [
    "Log modifiche",
    "Ruoli e permessi",
    "Storico cliente",
    "Allegati e documenti",
    "Dashboard cliente",
    "Processi ISO-ready",
];

const faqs = [
    {
        q: "TecSuite sostituisce il gestionale contabile?",
        a: "No. TecSuite è pensato per gestire l'operatività tecnica, commerciale e di servizio. La parte contabile può restare nel gestionale amministrativo già in uso.",
    },
    {
        q: "È adatto solo a Tecnologie?",
        a: "Nasce dall'esperienza reale di Tecnologie S.r.l., ma il modello è pensato per aziende IT, MSP e system integrator con esigenze simili.",
    },
    {
        q: "Posso gestire contratti prepagati e flat?",
        a: "Sì. TecSuite gestisce contratti a monte ore, contratti flat e servizi ricorrenti, con scadenze, consumi e storico.",
    },
    {
        q: "Il cliente può accedere a un portale?",
        a: "Sì. È prevista un'area cliente dove mostrare servizi, contratti, dispositivi, licenze, preventivi e documenti selezionati.",
    },
    {
        q: "Posso caricare PDF dei preventivi?",
        a: "Sì. I preventivi possono essere registrati come PDF, collegati al cliente, resi visibili o meno nell'area cliente e tracciati nello storico commerciale.",
    },
    {
        q: "Gestisce rapportini di intervento?",
        a: "Sì. TecSuite permette di pianificare attività, registrare sessioni di lavoro e generare rapportini PDF professionali.",
    },
    {
        q: "È un prodotto standard o personalizzabile?",
        a: "L'obiettivo è una piattaforma solida ma adattabile ai processi reali dell'azienda.",
    },
    {
        q: "È già pronto per la vendita?",
        a: "TecSuite è in evoluzione continua. La landing serve a presentare il prodotto e raccogliere richieste di demo/interesse.",
    },
];

export default function SecurityCtaFaq() {
    return (
        <>
            {/* SECURITY */}
            <section
                id="sicurezza"
                data-testid="security-section"
                className="relative bg-slate-50 py-24 sm:py-28 lg:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-6">
                            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                                <span className="h-px w-8 bg-teal-500" />
                                Sicurezza e affidabilità
                            </span>
                            <h2
                                data-testid="security-title"
                                className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                            >
                                Controllo, log e visibilità: ogni informazione deve essere tracciabile.
                            </h2>
                            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                                TecSuite è pensato per ambienti professionali dove la tracciabilità è fondamentale:
                                modifiche loggate, storico consultabile, dati organizzati per cliente e processi chiari.
                            </p>
                        </div>
                        <div className="lg:col-span-6">
                            <div
                                data-testid="security-badges"
                                className="rounded-3xl bg-white ring-1 ring-slate-200 p-6 sm:p-8 shadow-sm"
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-teal-400 text-white shadow-sm">
                                        <ShieldCheck className="w-5 h-5" strokeWidth={2.25} />
                                    </span>
                                    <div>
                                        <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                                            Progettato per
                                        </p>
                                        <p className="font-display font-semibold text-slate-900">
                                            Processi organizzati e tracciabili
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {securityBadges.map((b) => (
                                        <div
                                            key={b}
                                            data-testid={`security-badge-${b}`}
                                            className="flex items-center gap-2.5 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3.5 py-3"
                                        >
                                            <span className="h-2 w-2 rounded-full bg-gradient-to-br from-cyan-500 to-teal-400" />
                                            <span className="text-sm font-medium text-slate-800">
                                                {b}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA INTERMEDIA */}
            <section
                data-testid="cta-mid-section"
                className="relative bg-white py-16 sm:py-20"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B1120] via-[#0e1628] to-[#0f172a] px-8 sm:px-12 py-14 sm:py-16 grain">
                        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl" aria-hidden />
                        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl" aria-hidden />

                        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-8 text-white">
                                <h3
                                    data-testid="cta-mid-title"
                                    className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-tight"
                                >
                                    Vuoi vedere come TecSuite può adattarsi alla tua azienda IT?
                                </h3>
                                <p className="mt-4 text-slate-300 text-lg leading-relaxed max-w-2xl">
                                    Raccontaci come gestisci oggi interventi, contratti, licenze e clienti. Ti mostriamo
                                    come potrebbe diventare tutto più semplice in un&apos;unica piattaforma.
                                </p>
                            </div>
                            <div className="lg:col-span-4 flex lg:justify-end">
                                <a
                                    href="#contatti"
                                    data-testid="cta-mid-button"
                                    className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-teal-300 hover:from-cyan-300 hover:to-teal-200 text-slate-950 px-7 py-4 text-sm font-bold shadow-lg shadow-cyan-500/30 transition"
                                >
                                    Richiedi demo
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                id="faq"
                data-testid="faq-section"
                className="relative bg-slate-50 py-24 sm:py-28 lg:py-32"
            >
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <div className="text-center">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">
                            <span className="h-px w-8 bg-cyan-500" />
                            FAQ
                            <span className="h-px w-8 bg-cyan-500" />
                        </span>
                        <h2
                            data-testid="faq-title"
                            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                        >
                            Domande frequenti
                        </h2>
                    </div>

                    <Accordion
                        type="single"
                        collapsible
                        className="mt-12 space-y-3"
                        data-testid="faq-accordion"
                    >
                        {faqs.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                data-testid={`faq-item-${i}`}
                                className="rounded-2xl bg-white ring-1 ring-slate-200 px-5 sm:px-6 border-0"
                            >
                                <AccordionTrigger className="text-left font-display text-base sm:text-lg font-semibold text-slate-900 hover:no-underline py-5">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-[15px] leading-relaxed text-slate-600 pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </>
    );
}
